/**
 * テーマ（ダーク/ライト）の定義と初期値解決。
 * DOM 操作・localStorage へのアクセスは行わない純粋モジュールとして、
 * Node（Vitest）上で単体テストできるようにする。
 *
 * - CSS 側: `:root` の CSS 変数（style.css）が `data-theme` 属性で切り替わる。
 * - 3D 側: `SCENE_THEMES` のパレットを BoardScene.setTheme() が使う。
 */

export type ThemeName = 'dark' | 'light';

export const THEMES: readonly ThemeName[] = ['dark', 'light'];

/** localStorage に保存するキー */
export const THEME_STORAGE_KEY = 'gomoku3d-theme';

/** 3D シーン（BoardScene）がテーマごとに使う色・不透明度のパレット */
export interface SceneThemePalette {
  /** 背景色 */
  bg: number;
  /** 盤面グリッドの線色 */
  grid: number;
  /** 盤面グリッドの線の不透明度 */
  gridOpacity: number;
  /** 量子石のワイヤーフレーム殻の色 */
  shell: number;
  /** 量子石の殻の不透明度 */
  shellOpacity: number;
  /** ホバーハイライト（先手：黒を置く側） */
  hoverFirst: number;
  /** ホバーハイライト（後手：白を置く側） */
  hoverSecond: number;
  /** 断面ハイライト面の色 */
  section: number;
  /** 断面ハイライト枠線の色 */
  sectionBorder: number;
}

export const SCENE_THEMES: Record<ThemeName, SceneThemePalette> = {
  dark: {
    bg: 0x0f1218,
    grid: 0x8a93a6,
    gridOpacity: 0.16,
    shell: 0x9fb4ff,
    shellOpacity: 0.55,
    hoverFirst: 0x7f9dff,
    hoverSecond: 0xffffff,
    section: 0x4da3ff,
    sectionBorder: 0x7fd4ff,
  },
  light: {
    bg: 0xc7d1e0,
    grid: 0x46536b,
    gridOpacity: 0.3,
    shell: 0x3f5fc4,
    shellOpacity: 0.6,
    hoverFirst: 0x3557b7,
    hoverSecond: 0x5a6478,
    section: 0x2f6fd0,
    sectionBorder: 0x3557b7,
  },
};

/**
 * 初期テーマを決定する。
 * 保存済み値（localStorage）が有効ならそれを優先し、
 * なければシステム設定（prefers-color-scheme）に従う（未設定はダーク）。
 */
export function resolveInitialTheme(
  stored: string | null,
  prefersLight: boolean,
): ThemeName {
  if (stored === 'dark' || stored === 'light') return stored;
  return prefersLight ? 'light' : 'dark';
}
